<template>
  <div class="task-editor-container">
    <div class="header">
      <div class="return-btn" @click="handleCancel">
          &lt; <span>{{ $t('admin.rpa.btn.return') }}</span>
      </div>
      <div class="header-title">
          {{ $t(`admin.rpa.label.${isEditMode ? 'editAccount' : 'addAccount'}`) }}
      </div>
    </div>
    <div class="body">
      <FormEditor
        ref="formEditor"
        headerLable=""
        formWidth="450px"
        :labelWidth="labelWidth"
        :model="task"
        :formLines="formLines"
        :rules="formRules"
        @cancel="handleCancel"
        @save="handleSave">
        <template #isEnabled>
          <el-switch :value="task.isEnabled"></el-switch>
        </template>
        <template #company="{ line }">
          <el-select
            v-if="hasSuperAdminPermission"
            v-model="task.company"
            filterable
            remote
            :loading="fetchCompanyLoading"
            :remote-method="fetchCompanyInfo"
            :placeholder="$t('admin.rpa.placeholder.searchCompanyById')"
            @change="handleCompanySelect"
          >
            <el-option
              v-for="item in companyList"
              :key="item.id"
              :label="`${item.id} - ${item.i18nName}`"
              :value="item.id"
            />
          </el-select>
          <el-select
            v-else
            v-model="task.company"
            filterable
            :disabled="isEditMode"
            :placeholder="line.placeholder"
            @change="handleCompanySelect"
          >
            <el-option
              v-for="item in companyList"
              :key="item.id"
              :label="`${item.id} - ${item.i18nName}`"
              :value="item.id"
            />
          </el-select>
        </template>
        <template #channel>
          <el-radio-group v-model="task.channel" :disabled="isEditMode" @change="handleChannelChange">
            <el-radio :label="2">{{ $t('admin.rpa.label.51job') }}</el-radio>
            <el-radio :label="3">{{ $t('admin.rpa.label.zhilian') }}</el-radio>
            <el-radio :label="4">{{ $t('admin.rpa.label.liepin') }}</el-radio>
          </el-radio-group>
        </template>
        <template #featureType="{ line }">
          <el-select v-model="task.featureType" :disabled="isEditMode" :placeholder="line.placeholder">
            <el-option
              v-for="(item, index) in featureTypeOptionList"
              :key="index"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </template>
        <template #jobCompanyList>
          <el-select
            v-if="hasSuperAdminPermission"
            v-model="task.jobCompanyIds"
            multiple
            filterable
            remote
            :loading="fetchJobCompanyLoading"
            :remote-method="fetchJobCompanyInfo"
            :placeholder="$t('admin.rpa.placeholder.searchCompanyById')"
          >
            <el-option
              v-for="item in jobCompanyList"
              :key="item.id"
              :label="`${item.id} - ${item.i18nName}`"
              :value="item.id"
            />
          </el-select>
        </template>
        <template #ignoreNoIdJob>
          <el-switch v-model="task.ignoreNoIdJob"></el-switch>
        </template>
        <template #importResumeToClosedJob>
          <el-switch v-model="task.importResumeToClosedJob"></el-switch>
        </template>
      </FormEditor>
    </div>
  </div>
</template>

<script>
import FormEditor from '@/components/formEditor/common.vue'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import _ from 'lodash'
import { FEATURE_TYPE, featureTypeOptionList } from '@/constant/rpa'

export default {
  props: {
    taskBackend: { type: String, default: 'legacy' }
  },
  components: {
    FormEditor
  },
  data () {
    return {
      FEATURE_TYPE,
      i18nPrefix: 'admin.rpa',
      featureTypeOptionList,
      task: {
        id: null,
        isEnabled: true,
        channel: 2,
        company: null,
        companyInfo: { value: null },
        jobCompanyIds: [],
        featureType: 'DOWNLOAD_RESUME',
        memberNameFor51: null,
        account: null,
        password: null,
        ipAddress: null,
        ignoreNoIdJob: false,
        importResumeToClosedJob: true
      },
      ipAddress: null,
      companyList: [],
      fetchCompanyLoading: false,
      jobCompanyList: [],
      fetchJobCompanyLoading: false
    }
  },
  computed: {
    hasSuperAdminPermission () {
      return this.$parent.hasSuperAdminPermission
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    labelWidth () {
      if (this.$i18n.locale === 'en') {
        return '200px'
      } else {
        return '200px'
      }
    },
    formLines () {
      const vm = this
      const lines = [
        {
          name: 'channel',
          label: this.$t('admin.rpa.label.channel'),
          placeholder: this.$t('admin.rpa.message.pleaseInput'),
          slotName: 'channel'
        },
        {
          name: 'isEnabled',
          label: this.$t('admin.rpa.label.isEnabled'),
          slotName: 'isEnabled'
        },
        {
          name: 'featureType',
          label: this.$t('admin.rpa.label.featureType'),
          placeholder: this.$t('admin.rpa.message.pleaseSelect'),
          slotName: 'featureType'
        },
        {
          name: 'memberNameFor51',
          get hidden () {
            return vm.task.channel !== 2
          },
          label: this.$t('admin.rpa.label.memberName'),
          placeholder: this.$t('admin.rpa.message.pleaseInput'),
          slotName: 'memberName'
        },
        {
          name: 'account',
          label: this.$t('admin.rpa.label.account'),
          key: 'account',
          placeholder: this.$t('admin.rpa.message.pleaseInput')
        },
        {
          name: 'password',
          label: this.$t('admin.rpa.label.password'),
          key: 'password',
          placeholder: this.$t('admin.rpa.message.pleaseInput')
        },
        {
          name: 'company',
          label: this.$t('admin.rpa.label.companyName'),
          slotName: 'company'
        },
        {
          get hidden () {
            return vm.task.featureType !== FEATURE_TYPE.DOWNLOAD_RESUME
          },
          name: 'jobCompanyList',
          label: this.$t('admin.rpa.label.jobCompany'),
          slotName: 'jobCompanyList'
        },
        {
          name: 'ipAddress',
          label: this.$t('admin.rpa.label.ipAddress'),
          get disabled () {
            return vm.isEditMode || !vm.task.company || Boolean(vm.ipAddress)
          },
          get placeholder () {
            if (vm.isEditMode) {
              return ''
            } else if (!vm.task.company) {
              return vm.$t('admin.rpa.message.pleaseSelectCompany')
            } else {
              return vm.$t('admin.rpa.message.pleaseInput')
            }
          }
        },
        {
          name: 'ignoreNoIdJob',
          label: this.$t('admin.rpa.label.ignoreNoIdJob'),
          get hidden () {
            return vm.task.channel !== 2
          },
          slotName: 'ignoreNoIdJob'
        },
        {
          name: 'importResumeToClosedJob',
          label: this.$t('admin.rpa.label.importResumeToClosedJob'),
          get hidden () {
            return vm.task.featureType !== FEATURE_TYPE.DOWNLOAD_RESUME
          },
          slotName: 'importResumeToClosedJob'
        }
      ]
      return lines
    },
    formRules () {
      const memberNameFor51Check = (rule, value, callback) => {
        if (!value && this.task.channel === 2) {
          callback(new Error(this.$t('admin.rpa.message.pleaseInput')))
        } else {
          this.form.validateField('checkPass')
        }
        callback()
      }
      const ipAddressCheck = (rule, value, callback) => {
        const pattern = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
        if (value && !pattern.test(value)) {
          callback(new Error(this.$t('admin.rpa.message.ipAddressError')))
        }
        callback()
      }
      const rules = {
        company: [
          { required: true, message: this.$t('admin.rpa.message.pleaseSelect') }
        ],
        channel: [
          { required: true, message: this.$t('admin.rpa.message.pleaseSelect') }
        ],
        account: [
          { required: true, message: this.$t('admin.rpa.message.pleaseInput') }
        ],
        password: [
          { required: true, message: this.$t('admin.rpa.message.pleaseInput') }
        ],
        featureType: [
          { required: true, message: this.$t('admin.rpa.message.pleaseSelect') }
        ],
        memberNameFor51: [
          { required: true, validator: memberNameFor51Check }
        ],
        ipAddress: [
          { validator: ipAddressCheck, trigger: 'blur' }
        ]
      }
      return rules
    },
    form () {
      return this.$refs.formEditor.form
    }
  },
  async created () {
    if (this.isEditMode) {
      this.task.id = this.$route.params.taskId
      await this.getTaskAccountInfo()
    } else if (!this.hasSuperAdminPermission) {
      this.loadCompanyList()
    }
  },
  methods: {
    ...mapActions('company', ['getCompanyChildrenTree', 'searchCompanyInfo']),
    ...mapActions('rpa', ['getTaskById', 'saveTask', 'updateTask', 'getCompanyTaskIp']),
    async getTaskAccountInfo () {
      const taskId = this.$route.params.taskId
      this.showLoading = true
      try {
        const resp = await this.getTaskById({ taskId, taskBackend: this.taskBackend })
        if (resp) {
          this.formatDataToTask(resp)
        }
      } finally {
        this.$nextTick(() => this.form.clearValidate())
        this.showLoading = false
      }
    },
    formatDataToTask (originTaskData) {
      this.task = {
        id: originTaskData.id,
        isEnabled: originTaskData.is_enabled,
        company: originTaskData.company?.id,
        channel: originTaskData.channel?.id,
        featureType: originTaskData.feature_type,
        memberNameFor51: originTaskData.account?.job_51_user_name,
        account: originTaskData.account?.account,
        password: originTaskData.account?.password,
        ipAddress: originTaskData.ip_address,
        jobCompanyIds: [],
        ignoreNoIdJob: originTaskData.ignore_no_id_job,
        importResumeToClosedJob: originTaskData.import_resume_to_closed_job
      }
      this.companyList = [addI18nProperty(originTaskData.company, 'i18nName', 'cn_name', 'name')]
      if (this.hasSuperAdminPermission) {
        const jobCompanyList = []
        const jobCompanyIds = []
        _.forEach(originTaskData.job_company_list, item => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'name')
          jobCompanyList.push(item)
          jobCompanyIds.push(item.id)
        })
        this.jobCompanyList = jobCompanyList
        this.task.jobCompanyIds = jobCompanyIds
      }
    },
    async handleCompanySelect () {
      await this.loadCompanyIp()
      if (this.ipAddress) {
        this.task.ipAddress = this.ipAddress
      }
    },
    async loadCompanyIp () {
      const params = {
        companyId: this.task.company
      }
      try {
        this.$eventBus.$emit('showLoading', true)
        const resp = await this.getCompanyTaskIp(params)
        if (resp) {
          this.ipAddress = resp.ip_address
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleCancel () {
      this.$router.go(-1)
    },
    async handleSave () {
      let result = false
      this.form.validate((isSuccess, failItems) => {
        result = isSuccess
      })
      if (!result) {
        return
      }
      try {
        const task = {
          is_enabled: this.task.isEnabled,
          feature_type: this.task.featureType,
          channel_id: this.task.channel,
          company_id: this.task.company,
          job_51_user_name: this.task.memberNameFor51,
          account: this.task.account,
          password: this.task.password,
          job_company_ids: this.task.jobCompanyIds,
          ip_address: this.task.ipAddress,
          ignore_no_id_job: this.task.ignoreNoIdJob,
          import_resume_to_closed_job: this.task.importResumeToClosedJob
        }
        let saveFunction = null
        if (this.isEditMode) {
          task.id = this.task.id
          saveFunction = this.updateTask
        } else {
          saveFunction = this.saveTask
        }
        const resp = await saveFunction({ ...task, taskBackend: this.taskBackend })
        if (resp) {
          this.$router.push({ name: this.taskBackend === 'new' ? 'rpaNewDashboardIndex' : 'rpaDashboardIndex' })
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    flatCompanyChildrenTreeMap (resultList, company, treeMap) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      resultList.push(company)
      _.forEach(treeMap[company.id], child => {
        this.flatCompanyChildrenTreeMap(resultList, child, treeMap)
      })
    },
    async loadCompanyList () {
      if (!this.companyListLoader) {
        this.companyListLoader = this.getCompanyChildrenTree().then(resp => {
          const currentCompany = resp.self
          const childrenMap = resp.children
          const rootCompany = resp.root
          const companyList = []
          this.flatCompanyChildrenTreeMap(companyList, currentCompany, childrenMap)
          this.companyList = companyList
          this.company = currentCompany
          this.rootCompany = rootCompany
        })
      }
      await this.companyListLoader
      return this.companyList
    },
    handleChannelChange (type) {
      this.$nextTick(() => this.form.clearValidate())
    },
    async fetchCompanyInfo (queryString) {
      this.fetchCompanyLoading = true
      try {
        const companyId = parseInt(queryString)
        if (!companyId) {
          return
        }
        if (queryString && !isNaN(parseInt(queryString))) {
          const resp = await this.searchCompanyInfo({ id: parseInt(queryString) })
          this.companyList = _.map(resp, item => addI18nProperty(item, 'i18nName', 'cn_name', 'name'))
        }
      } finally {
        this.fetchCompanyLoading = false
      }
    },
    async fetchJobCompanyInfo (queryString) {
      this.fetchJobCompanyLoading = true
      try {
        this.jobCompanyList = _.filter(this.jobCompanyList, item => this.task.jobCompanyIds.includes(item.id))
        const companyId = parseInt(queryString)
        if (!companyId) {
          return
        }
        const found = _.find(this.jobCompanyList, { id: companyId })
        if (found) {
          return
        }
        const resp = await this.searchCompanyInfo({ id: parseInt(queryString) })
        _.forEach(resp, (item, index) => {
          const found = _.find(this.jobCompanyList, { id: item.id })
          if (found) {
            return
          }
          addI18nProperty(item, 'i18nName', 'cn_name', 'name')
          this.jobCompanyList.splice(0, index, item)
        })
      } finally {
        this.fetchJobCompanyLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.task-editor-container {
  .header {
    .return-btn {
      cursor: pointer;
      margin-top: 20px;

      > span {
        margin-left: 10px;
      }
    }

    .header-title {
      background-color: #FFFFFF;
      padding: 20px;
      margin: 10px 0;
    }
  }

  .body {
    background-color: #FFFFFF;
    padding: 20px 0;

    ::v-deep .form-editor-wrapper {
      .el-divider {
        display: none;
      }

      .el-form {
        margin-left: 10%;

        .el-autocomplete {
          width: 100%;
        }
      }

      .bottom-btn-group {
        width: 600px;
        padding-left: 10%;
      }
    }

  }
}
</style>
